/**
 * Step2QualifiersCard
 * Optional step after Segment selection - Likely Qualifiers (Targeting Profile)
 * 
 * Generates probabilistic targeting hints for:
 * - Ad targeting setup (Meta / Search / LinkedIn / Partnerships)
 * - Creative tone choices
 * - Landing-page personalization context
 */

import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Checkbox } from '../../../components/ui/checkbox';
import { Textarea } from '../../../components/ui/textarea';
import { 
  Target, Check, Loader2, ChevronDown, ChevronUp, 
  Sparkles, AlertTriangle, Users, Building2, User,
  SkipForward, Save, Info, Eye, EyeOff, RefreshCw
} from 'lucide-react';
import { plansApi } from '../api/plansApi';

// Confidence colors
const CONFIDENCE_COLORS = {
  high: 'bg-green-500/10 text-green-700 border-green-300',
  medium: 'bg-amber-500/10 text-amber-700 border-amber-300',
  low: 'bg-gray-500/10 text-gray-600 border-gray-300'
};

// Mode badge colors
const MODE_COLORS = {
  b2c: 'bg-blue-500/10 text-blue-700 border-blue-300',
  b2b: 'bg-purple-500/10 text-purple-700 border-purple-300',
  hybrid: 'bg-teal-500/10 text-teal-700 border-teal-300'
};

// Qualifier group labels
const GROUP_LABELS = {
  // B2C
  age_bands: 'Age Range',
  income_bands: 'Income Level',
  gender_skew: 'Gender',
  household: 'Household Type',
  homeownership: 'Homeownership',
  life_stage: 'Life Stage',
  device_skew: 'Device Preference',
  geo_relevance: 'Geographic Scope',
  // B2B
  company_type: 'Company Type',
  company_size: 'Company Size',
  industry_verticals: 'Industry',
  buyer_roles: 'Buyer Role',
  function: 'Function',
  seniority: 'Seniority',
  buying_committee: 'Buying Committee',
  budget_sensitivity: 'Budget Sensitivity',
  implementation_friction: 'Implementation Effort',
  tech_stack_hints: 'Tech Stack'
};

// Helper to extract value from qualifier item
function getItemValue(item) {
  if (item.band) return item.band;
  if (item.group) return item.group;
  if (item.stage) return item.stage;
  if (item.device) return item.device;
  if (item.geo) return item.geo;
  if (item.type) return item.type;
  if (item.industry) return item.industry;
  if (item.role) return item.role;
  if (item.function) return item.function;
  if (item.level) return item.level;
  if (item.pattern) return item.pattern;
  if (item.stack) return item.stack;
  return 'unknown';
}

// Qualifier Item Component
function QualifierItem({ item, fieldKey, isEnabled, onToggle }) {
  const value = getItemValue(item);
  const likelihood = Math.round((item.likelihood || 0) * 100);
  const confidenceLevel = likelihood >= 70 ? 'high' : likelihood >= 40 ? 'medium' : 'low';
  
  return (
    <div className={`flex items-center justify-between p-2 rounded-lg border transition-colors ${
      isEnabled ? 'border-purple-300 bg-purple-500/5' : 'border-border'
    }`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <Checkbox
          id={`${fieldKey}-${value}`}
          checked={isEnabled}
          onCheckedChange={onToggle}
          className="shrink-0"
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm capitalize">
              {value.replace(/_/g, ' ')}
            </span>
            <Badge 
              variant="outline" 
              className={`text-xs ${CONFIDENCE_COLORS[confidenceLevel]}`}
            >
              {likelihood}%
            </Badge>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate" title={item.why}>
            {item.why}
          </p>
        </div>
      </div>
    </div>
  );
}

// Qualifier Group Component
function QualifierGroup({ groupKey, items, selectedItems, onToggleItem, maxVisible = 2 }) {
  const [showAll, setShowAll] = useState(false);
  
  if (!items || items.length === 0) return null;
  
  // Sort by likelihood descending
  const sortedItems = [...items].sort((a, b) => (b.likelihood || 0) - (a.likelihood || 0));
  const visibleItems = showAll ? sortedItems : sortedItems.slice(0, maxVisible);
  const hasMore = sortedItems.length > maxVisible;
  
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-slate-700">{GROUP_LABELS[groupKey] || groupKey}</h4>
      <div className="space-y-2">
        {visibleItems.map((item, idx) => {
          const itemKey = getItemValue(item) !== 'unknown' ? getItemValue(item) : idx;
          const isEnabled = selectedItems && selectedItems[groupKey] && selectedItems[groupKey].includes(itemKey);
          
          return (
            <QualifierItem
              key={`${groupKey}-${idx}`}
              item={item}
              fieldKey={groupKey}
              isEnabled={isEnabled}
              onToggle={() => onToggleItem(groupKey, itemKey)}
            />
          );
        })}
      </div>
      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setShowAll(!showAll)}
          className="w-full text-xs text-muted-foreground"
        >
          {showAll ? (
            <>
              <EyeOff className="w-3 h-3 mr-1" />
              Show less
            </>
          ) : (
            <>
              <Eye className="w-3 h-3 mr-1" />
              Show all ({sortedItems.length - maxVisible} more)
            </>
          )}
        </Button>
      )}
    </div>
  );
}

// B2C Qualifiers Section
function B2CQualifiers({ mode, qualifiers, selectedItems, onToggleItem }) {
  if (!mode) return null;
  if (mode.type !== 'b2c' && mode.type !== 'hybrid') return null;
  if (!qualifiers || !qualifiers.likely_profile || !qualifiers.likely_profile.b2c) return null;
  
  const b2cData = qualifiers.likely_profile.b2c;
  const entries = Object.entries(b2cData).filter(([_, items]) => items && items.length > 0);
  
  if (entries.length === 0) return null;
  
  return (
    <div className="space-y-4">
      <h4 className="font-medium text-sm flex items-center gap-2">
        <User className="w-4 h-4 text-blue-600" />
        Consumer Profile (B2C)
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {entries.map(([key, items]) => (
          <QualifierGroup
            key={key}
            groupKey={key}
            items={items}
            selectedItems={selectedItems}
            onToggleItem={onToggleItem}
          />
        ))}
      </div>
    </div>
  );
}

// B2B Qualifiers Section
function B2BQualifiers({ mode, qualifiers, selectedItems, onToggleItem }) {
  if (!mode) return null;
  if (mode.type !== 'b2b' && mode.type !== 'hybrid') return null;
  if (!qualifiers || !qualifiers.likely_profile || !qualifiers.likely_profile.b2b) return null;
  
  const b2bData = qualifiers.likely_profile.b2b;
  const entries = Object.entries(b2bData).filter(([_, items]) => items && items.length > 0);
  
  if (entries.length === 0) return null;
  
  return (
    <div className="space-y-4">
      <h4 className="font-medium text-sm flex items-center gap-2">
        <Building2 className="w-4 h-4 text-purple-600" />
        Business Profile (B2B)
      </h4>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {entries.map(([key, items]) => (
          <QualifierGroup
            key={key}
            groupKey={key}
            items={items}
            selectedItems={selectedItems}
            onToggleItem={onToggleItem}
          />
        ))}
      </div>
    </div>
  );
}

// Validation Checks Section
function ValidationChecks({ checks }) {
  if (!checks || checks.length === 0) return null;
  
  return (
    <div className="space-y-2 p-3 bg-slate-50 rounded-lg">
      <h4 className="font-medium text-sm text-slate-700">Validation Checks</h4>
      <div className="space-y-2">
        {checks.map((check, idx) => (
          <div key={idx} className="text-xs text-muted-foreground">
            <span className="font-medium">{check.name}:</span> {check.action}
          </div>
        ))}
      </div>
    </div>
  );
}

// Main Component
export default function Step2QualifiersCard({
  planId,
  stepData,
  step1Data,
  step2Data,
  isCompleted,
  onSave,
  onSkip,
  saving
}) {
  const [generating, setGenerating] = useState(false);
  const [qualifiers, setQualifiers] = useState(null);
  const [selectedItems, setSelectedItems] = useState({});
  const [userHints, setUserHints] = useState('');
  const [showHints, setShowHints] = useState(false);
  const [expanded, setExpanded] = useState(!isCompleted);
  
  // Get selected persona and segment from previous steps
  const selectedPersonaIds = step1Data?.selected_persona_ids || [];
  const planPersonas = step1Data?.plan_personas || [];
  const selectedSegmentIds = step2Data?.selected_segment_ids || [];
  const generatedSegments = step2Data?.generated_segments || [];
  
  // Find the selected persona and segment
  const selectedPersona = planPersonas.find(p => 
    selectedPersonaIds.includes(p.persona_id || p.id)
  );
  const selectedSegment = generatedSegments.find(s => 
    selectedSegmentIds.includes(s.segment_id || s.id)
  );
  
  // Check if exactly one segment is selected
  const canGenerate = selectedSegmentIds.length === 1 && selectedPersona && selectedSegment;
  const multipleSegmentsSelected = selectedSegmentIds.length > 1;
  
  // Load existing data
  useEffect(() => {
    if (stepData?.qualifiers_selected) {
      setSelectedItems(stepData.qualifiers_selected);
    }
  }, [stepData]);

  // Load existing generated qualifiers from backend
  useEffect(() => {
    const loadExistingQualifiers = async () => {
      if (planId) {
        try {
          const data = await plansApi.getQualifiers(planId);
          if (data.qualifiers_generated_raw) {
            setQualifiers(data.qualifiers_generated_raw);
          }
          if (data.qualifiers_selected) {
            setSelectedItems(data.qualifiers_selected);
          }
        } catch (error) {
          // No existing qualifiers, that's fine
          console.log('No existing qualifiers found');
        }
      }
    };
    loadExistingQualifiers();
  }, [planId]);
  
  // Generate qualifiers
  const handleGenerate = async () => {
    if (!canGenerate) {
      if (multipleSegmentsSelected) {
        toast.error('Select exactly 1 segment to generate qualifiers');
      }
      return;
    }
    
    setGenerating(true);
    try {
      const result = await plansApi.generateQualifiers(
        planId,
        selectedPersona,
        selectedSegment,
        userHints || null
      );
      
      if (result.qualifiers) {
        setQualifiers(result.qualifiers);
        toast.success('Qualifiers generated');
      } else {
        toast.error('Failed to generate qualifiers');
      }
    } catch (error) {
      console.error('Generate qualifiers error:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate qualifiers');
    } finally {
      setGenerating(false);
    }
  };
  
  // Toggle a qualifier item
  const handleToggleItem = (groupKey, itemKey) => {
    setSelectedItems(prev => {
      const current = prev[groupKey] || [];
      const updated = current.includes(itemKey)
        ? current.filter(k => k !== itemKey)
        : [...current, itemKey];
      
      return {
        ...prev,
        [groupKey]: updated
      };
    });
  };
  
  // Save selected qualifiers
  const handleSave = async () => {
    try {
      await plansApi.saveQualifiers(planId, selectedItems);
      toast.success('Qualifiers saved');
      onSave?.({ qualifiers_selected: selectedItems, skipped: false });
    } catch (error) {
      console.error('Save qualifiers error:', error);
      toast.error(error.response?.data?.detail || 'Failed to save qualifiers');
    }
  };
  
  // Skip qualifiers
  const handleSkip = async () => {
    try {
      await plansApi.skipQualifiers(planId);
      toast.success('Qualifiers step skipped');
      onSkip?.();
    } catch (error) {
      console.error('Skip qualifiers error:', error);
      toast.error(error.response?.data?.detail || 'Failed to skip');
    }
  };
  
  // Regenerate qualifiers with existing selections as context
  const handleRegenerate = async () => {
    if (!canGenerate) {
      if (multipleSegmentsSelected) {
        toast.error('Select exactly 1 segment to regenerate qualifiers');
      }
      return;
    }
    
    setGenerating(true);
    try {
      // Build context from existing selections
      const existingSelectionsContext = Object.entries(selectedItems)
        .filter(([_, items]) => items && items.length > 0)
        .map(([group, items]) => `${GROUP_LABELS[group] || group}: ${items.join(', ')}`)
        .join('; ');
      
      const regenerateHints = existingSelectionsContext 
        ? `Previously selected qualifiers to consider: ${existingSelectionsContext}. ${userHints || ''}`
        : userHints || null;
      
      const result = await plansApi.generateQualifiers(
        planId,
        selectedPersona,
        selectedSegment,
        regenerateHints
      );
      
      if (result.qualifiers) {
        setQualifiers(result.qualifiers);
        // Keep existing selections that still apply
        toast.success('Qualifiers regenerated');
      } else {
        toast.error('Failed to regenerate qualifiers');
      }
    } catch (error) {
      console.error('Regenerate qualifiers error:', error);
      toast.error(error.response?.data?.detail || 'Failed to regenerate qualifiers');
    } finally {
      setGenerating(false);
    }
  };
  
  // Render mode badge
  const mode = qualifiers?.mode;
  const ModeIcon = mode?.type === 'b2b' ? Building2 : mode?.type === 'b2c' ? User : Users;
  
  // Completed compact view
  if (isCompleted && !expanded) {
    const wasSkipped = stepData?.skipped;
    
    return (
      <div className="bg-background rounded-xl border border-border p-4">
        <div 
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(true)}
        >
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-teal-500/10 flex items-center justify-center">
              <Target className="w-4 h-4 text-teal-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-sm">Likely Qualifiers</h3>
                <Badge variant="outline" className="text-xs bg-green-500/10 text-green-700 border-green-300">
                  <Check className="w-3 h-3 mr-1" />
                  {wasSkipped ? 'Skipped' : 'Saved'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {wasSkipped 
                  ? 'No targeting hints applied' 
                  : `${Object.keys(stepData?.qualifiers_selected || {}).length} qualifier groups enabled`
                }
              </p>
            </div>
          </div>
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="bg-background rounded-xl border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border bg-slate-50/50">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-teal-500/10 flex items-center justify-center">
              <Target className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">Likely Qualifiers</h3>
                <Badge variant="outline" className="text-xs">Optional</Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                Targeting profile hints for the selected segment
              </p>
            </div>
          </div>
          {isCompleted && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(false)}
            >
              <ChevronUp className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Segment selection check */}
        {multipleSegmentsSelected && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-700">
              Select exactly 1 segment to generate qualifiers. You have {selectedSegmentIds.length} selected.
            </p>
          </div>
        )}
        
        {!canGenerate && !multipleSegmentsSelected && !qualifiers && (
          <div className="flex items-center gap-2 p-3 bg-slate-50 border border-border rounded-lg">
            <Info className="w-4 h-4 text-slate-500 shrink-0" />
            <p className="text-sm text-muted-foreground">
              Complete Step 2 with exactly one segment selected to generate qualifiers.
            </p>
          </div>
        )}
        
        {/* User hints input */}
        {canGenerate && !qualifiers && (
          <div className="space-y-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHints(!showHints)}
              className="text-xs text-muted-foreground"
            >
              {showHints ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
              Add targeting hints (optional)
            </Button>
            
            {showHints && (
              <Textarea
                value={userHints}
                onChange={(e) => setUserHints(e.target.value)}
                placeholder="Any specific targeting constraints or insights? e.g., 'primarily targeting enterprise, focus on marketing teams'"
                className="text-sm resize-none"
                rows={2}
              />
            )}
            
            <Button
              onClick={handleGenerate}
              disabled={generating}
              className="w-full bg-teal-600 hover:bg-teal-700"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Generate Qualifiers
                </>
              )}
            </Button>
          </div>
        )}
        
        {/* Generated qualifiers */}
        {qualifiers && (
          <div className="space-y-4">
            {/* Mode detection */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                mode?.type === 'b2b' ? 'bg-purple-100' : 
                mode?.type === 'b2c' ? 'bg-blue-100' : 'bg-teal-100'
              }`}>
                <ModeIcon className={`w-4 h-4 ${
                  mode?.type === 'b2b' ? 'text-purple-600' : 
                  mode?.type === 'b2c' ? 'text-blue-600' : 'text-teal-600'
                }`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-sm">
                    {mode?.type === 'b2b' ? 'B2B' : mode?.type === 'b2c' ? 'B2C' : 'Hybrid'} Detected
                  </span>
                  <Badge variant="outline" className={`text-xs ${MODE_COLORS[mode?.type || 'hybrid']}`}>
                    {Math.round((mode?.confidence || 0) * 100)}% confidence
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">{mode?.why}</p>
              </div>
            </div>
            
            {/* Summary */}
            {qualifiers.summary && (
              <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <p className="font-medium text-sm text-teal-800 mb-2">
                  {qualifiers.summary.one_liner}
                </p>
                <div className="space-y-1">
                  {qualifiers.summary.how_to_use?.map((hint, idx) => (
                    <p key={idx} className="text-xs text-teal-700">• {hint}</p>
                  ))}
                </div>
              </div>
            )}
            
            {/* B2C Qualifiers */}
            <B2CQualifiers
              mode={mode}
              qualifiers={qualifiers}
              selectedItems={selectedItems}
              onToggleItem={handleToggleItem}
            />
            
            {/* B2B Qualifiers */}
            <B2BQualifiers
              mode={mode}
              qualifiers={qualifiers}
              selectedItems={selectedItems}
              onToggleItem={handleToggleItem}
            />
            
            {/* Validation checks */}
            <ValidationChecks checks={qualifiers.validation_checks} />
            
            {/* Safety disclaimer */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-700">
                {qualifiers.safety?.disclaimer || 
                 'These are probabilistic guesses for targeting/creative only. Do not treat as facts.'}
              </p>
            </div>
            
            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button
                onClick={handleSave}
                disabled={saving || generating}
                className="flex-1"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Qualifiers
              </Button>
              <Button
                variant="outline"
                onClick={handleRegenerate}
                disabled={saving || generating || !canGenerate}
                title="Regenerate with current selections as context"
              >
                {generating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={saving || generating}
                className="text-muted-foreground"
              >
                <SkipForward className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
        
        {/* Skip button when no qualifiers yet */}
        {!qualifiers && canGenerate && (
          <Button
            variant="ghost"
            onClick={handleSkip}
            className="w-full text-muted-foreground"
          >
            <SkipForward className="w-4 h-4 mr-2" />
            Skip qualifiers
          </Button>
        )}
      </div>
    </div>
  );
}
